import { skipToken, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useSnackbar } from "../contexts/SnackbarContext";
import { backendService } from "../services/backend.service";
import type { DeactivationImpactResponse } from "../utils/models";

const DEACTIVATION_IMPACT_QUERY_KEY_ROOT = "deactivationImpact";

/**
 * Whether deactivating the nurse on `deactivationDate` would remove shifts or recurring templates in the
 * department. Asked of the server, which checks the same rows deactivation deletes — the schedule the page
 * has loaded may not reach the end of the created schedule, so it can't answer this on its own.
 *
 * `nurseId` undefined means no nurse is being deactivated yet; nothing is asked.
 */
export const useDeactivationImpact = (departmentId: string, nurseId: number | undefined, deactivationDate: string) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { trackError } = useAnalytics();

	const query = useQuery<DeactivationImpactResponse>({
		queryKey: [DEACTIVATION_IMPACT_QUERY_KEY_ROOT, departmentId, nurseId, deactivationDate],
		queryFn:
			nurseId === undefined
				? skipToken
				: async () => {
						const response = await backendService.getDeactivationImpact(departmentId, nurseId, deactivationDate);
						return response.data;
					},
	});

	useEffect(() => {
		if (query.isError) {
			trackError("deactivation_impact_fetch_error", { department_id: departmentId, nurse_id: nurseId ?? null });
			showSnackbar(t("team.deactivate.check_failed"), "error");
		}
	}, [query.isError, departmentId, nurseId, trackError, showSnackbar, t]);

	return {
		impact: query.data,
		// `isFetching`, not `isLoading`: a cached answer can be out of date by the time the dialog reopens, so a
		// refetch in flight also counts as "not known yet".
		isChecking: query.isFetching,
		isError: query.isError,
	};
};
